import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Download, Info } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InfoModal from "../components/InfoModal";
import DownloadWarningModal from "../components/DownloadWarningModal";

// Helper function to group entries by quarter
const groupByQuarter = (entries) => {
  const quarters = {};
  entries.forEach(entry => {
    const date = new Date(entry.date);
    const year = date.getFullYear();
    const quarter = Math.floor((date.getMonth()) / 3) + 1;
    const key = `${year}-Q${quarter}`;
    
    if (!quarters[key]) {
      quarters[key] = [];
    }
    quarters[key].push(entry);
  });
  
  // Sort quarters descending (newest first)
  return Object.keys(quarters)
    .sort((a, b) => b.localeCompare(a))
    .map(key => ({
      quarter: key,
      entries: quarters[key].sort((a, b) => new Date(b.date) - new Date(a.date))
    }));
};

export default function Eigenverbrauch() {
  const [activeTab, setActiveTab] = useState("geschäftlich");
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    article: "",
    quantity: "1",
    ek_price: "",
    type: "geschäftlich"
  });

  useEffect(() => {
    loadEntries();
    loadProducts();
    loadStock();
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowForm(false);
    setFormData({
      ...formData,
      type: tab,
      article: "",
      ek_price: ""
    });
  };

  const loadEntries = async () => {
    try {
      const typeParam = activeTab === "gesamt" ? "" : `?type=${encodeURIComponent(activeTab)}`;
      const response = await axios.get(`${API}/personal-use${typeParam}`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Produkte");
    }
  };

  const loadStock = async () => {
    try {
      // Load stock filtered by current tab type
      const typeParam = activeTab === "gesamt" ? "" : `?type=${encodeURIComponent(activeTab)}`;
      const response = await axios.get(`${API}/stock${typeParam}`);
      setStock(response.data);
    } catch (error) {
      console.error("Fehler beim Laden des Bestands");
    }
  };

  const handleProductSelect = (productName) => {
    const product = products.find(p => p.name === productName);
    const stockItem = stock.find(s => s.article === productName);
    
    if (product && stockItem) {
      setFormData({
        ...formData,
        article: product.name,
        ek_price: stockItem.avg_ek_price.toString() || "0"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/personal-use`, {
        ...formData,
        quantity: parseInt(formData.quantity),
        ek_price: parseFloat(formData.ek_price)
      });
      
      toast.success("Eigenverbrauch erfasst");
      setFormData({
        date: new Date().toISOString().split('T')[0],
        article: "",
        quantity: "1",
        ek_price: "",
        type: activeTab
      });
      setShowForm(false);
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/personal-use/${id}`);
      toast.success("Eintrag gelöscht");
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const requestDownloadPDF = (quarter) => {
    setPendingDownload(quarter);
    setShowDownloadWarning(true);
  };

  const confirmDownload = async () => {
    if (!pendingDownload) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/personal-use/export/${pendingDownload}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Eigenverbrauch_${pendingDownload}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF wird heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    } finally {
      setShowDownloadWarning(false);
      setPendingDownload(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Inventory
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Eigenverbrauch</h1>
                <p className="text-slate-500 mt-1 text-sm sm:text-base">Eigenverbrauch erfassen (reduziert Bestand)</p>
              </div>
              <Button 
                onClick={() => setShowInfoModal(true)}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200 w-full sm:w-auto"
              >
                <Info className="w-4 h-4 mr-2" />
                Kategorie-Hilfe
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
            <button
              onClick={() => handleTabChange("geschäftlich")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "geschäftlich"
                  ? "text-slate-600 border-b-2 border-slate-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Geschäftlich
            </button>
            <button
              onClick={() => handleTabChange("haushalt")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "haushalt"
                  ? "text-slate-600 border-b-2 border-slate-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Haushalt
            </button>
            <button
              onClick={() => handleTabChange("gesamt")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "gesamt"
                  ? "text-slate-600 border-b-2 border-slate-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Gesamt
            </button>
          </div>

          {activeTab !== "gesamt" && (
            <Button 
              onClick={() => setShowForm(!showForm)}
              className="bg-gradient-to-r from-slate-600 to-gray-600 hover:from-slate-700 hover:to-gray-700 mb-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Eintrag hinzufügen
            </Button>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <h3 className="text-lg font-semibold mb-4">Neuer Eigenverbrauch</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="date">Datum *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="article">Artikel (nur Bestand) *</Label>
                  <Select onValueChange={handleProductSelect} value={formData.article}>
                    <SelectTrigger>
                      <SelectValue placeholder="Artikel wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {products
                        .filter(product => {
                          const stockItem = stock.find(s => s.article === product.name);
                          return stockItem && stockItem.stock > 0;
                        })
                        .map((product) => {
                          const stockItem = stock.find(s => s.article === product.name);
                          return (
                            <SelectItem key={product.id} value={product.name}>
                              {product.name} (Bestand: {stockItem?.stock || 0})
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="quantity">Menge *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    required
                    placeholder="1"
                  />
                </div>
                <div>
                  <Label htmlFor="ek_price">Ø EK-Preis (automatisch) *</Label>
                  <Input
                    id="ek_price"
                    type="number"
                    step="0.01"
                    value={formData.ek_price}
                    readOnly
                    required
                    placeholder="Wird automatisch gesetzt"
                    className="bg-slate-100 cursor-not-allowed"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button type="submit" className="bg-slate-600 hover:bg-slate-700">
                  Speichern
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-8">
            {groupByQuarter(entries).length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Noch kein Eigenverbrauch erfasst
              </div>
            )}
            
            {groupByQuarter(entries).map((quarterGroup) => (
              <div key={quarterGroup.quarter} className="bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex justify-between items-center p-4 bg-slate-100 rounded-t-lg border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">
                    {quarterGroup.quarter.replace('Q', '. Quartal ')}
                  </h3>
                  {activeTab === "geschäftlich" && (
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => requestDownloadPDF(quarterGroup.quarter)}
                      className="bg-white hover:bg-slate-50"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF Export
                    </Button>
                  )}
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Datum</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Artikel</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Menge</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">EK-Preis</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">EK-Ges</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterGroup.entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-slate-100 hover:bg-white">
                          <td className="py-3 px-4">{new Date(entry.date).toLocaleDateString('de-DE')}</td>
                          <td className="py-3 px-4">{entry.article}</td>
                          <td className="py-3 px-4 text-right">{entry.quantity}</td>
                          <td className="py-3 px-4 text-right">{entry.ek_price.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-right font-semibold">{entry.ek_total.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-right">
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDelete(entry.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-b-lg border-t border-slate-200">
                  <div className="text-sm text-slate-600">
                    <strong>Quartal Zusammenfassung:</strong> {quarterGroup.entries.length} Einträge, 
                    Gesamtwert: <span className="font-semibold text-slate-600">
                      {quarterGroup.entries.reduce((sum, entry) => sum + entry.ek_total, 0).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <InfoModal 
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          category="privatnutzung"
        />

        <DownloadWarningModal 
          isOpen={showDownloadWarning}
          onClose={() => {
            setShowDownloadWarning(false);
            setPendingDownload(null);
          }}
          onConfirm={confirmDownload}
          title="Eigenverbrauch PDF herunterladen"
        />
      </div>
    </div>
  );
}